"use client"

import { useRef, useState, Suspense } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { HerbicideTimingForm } from "@/components/tools/herbicide-timing-form"
import { ProductRecommendations } from "@/components/tools/product-recommendations"
import { HerbicideTimingResults } from "@/components/tools/herbicide-timing-results"
import { type HerbicideTimingResult } from "@/lib/herbicide-timing-calculator"
import { Card } from "@/components/ui/card"
import { HeadlineHero, LawnPrefillBanner, RelatedTools, ResultSuccessCard, ResultEmailCaptureCard, ToolNextStepCard, ToolPageLoadingFallback } from "@/components/tools/shared"
import { buildRelatedToolUrl, buildToolUrl } from "@/lib/result-tool-urls"
import { toolNavigationAnalytics, analytics, stashEmailCaptureAttribution } from "@/lib/analytics"
import { useAuth } from "@/contexts/AuthContext"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import { ProductGuideCta } from "@/components/lead-magnets/product-guide-cta"
import { useDefaultLawn } from "@/hooks/use-default-lawn"
import { useFromTool } from "@/hooks/use-from-tool"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { useCalculatorStarted } from "@/hooks/useCalculatorStarted"
import { WEED_CHARACTERISTICS } from "@/data/weed-types"

function HerbicideTimingContent() {
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool("herbicide-timing")
  const { subscription } = useAuth()
  const { lawns, selectedLawnId, setSelectedLawnId, prefill: lawnPrefill } = useDefaultLawn()
  const { trackUsage, showNewsletterModal, closeNewsletterModal, usageCount } = useNewsletterPrompt()
  const [result, setResult] = useState<HerbicideTimingResult | null>(null)
  const [weedsWereAutoPicked, setWeedsWereAutoPicked] = useState(false)
  // 'default' means result.grassType is the region guess behind the "Not Sure"
  // option, not a grass the user gave us. See HerbicideTimingForm's prop docs.
  const [grassTypeSource, setGrassTypeSource] = useState<'user' | 'default'>('default')
  const trackedUsageRef = useRef(false)
  // Fires calculator_started on first input — this tool gates the result on a
  // resolved ZIP, so calculator_used can't measure pre-ZIP abandonment.
  const startedRef = useCalculatorStarted('herbicide')

  // The form computes the result live (once the ZIP resolves to a known region)
  // and streams it up. Usage analytics fire exactly once per session via the
  // ref guard, preserving the same events the old submit path fired.
  const handleResultChange = (
    calculationResult: HerbicideTimingResult | null,
    autoPicked: boolean,
    resolvedGrassSource: 'user' | 'default',
  ) => {
    setResult(calculationResult)
    setWeedsWereAutoPicked(autoPicked)
    setGrassTypeSource(resolvedGrassSource)
    if (calculationResult && !trackedUsageRef.current) {
      trackedUsageRef.current = true
      trackUsage('herbicide-timing')
      analytics.calculatorUsed('herbicide', {
        inputs: {
          grass_type: calculationResult.grassType,
          zip_code: calculationResult.zipCode,
          target_weeds: calculationResult.targetWeeds,
          application_type: calculationResult.applicationType,
        },
        result: {
          region: calculationResult.region,
          recommended_approach: calculationResult.recommendedApproach,
          pre_emergent_window: calculationResult.preEmergentTiming?.optimalWindow,
          pre_emergent_critical_date: calculationResult.preEmergentTiming?.criticalDate,
          pre_emergent_soil_temp_req: calculationResult.preEmergentTiming?.soilTempRequirement,
          pre_emergent_urgency: calculationResult.preEmergentTiming?.urgencyLevel,
          pre_emergent_days_remaining: calculationResult.preEmergentTiming?.daysRemaining,
          pre_emergent_status: calculationResult.preEmergentTiming?.status,
          post_emergent_window: calculationResult.postEmergentTiming?.optimalWindow,
          post_emergent_current_conditions: calculationResult.postEmergentTiming?.currentConditions,
          post_emergent_growth_stage: calculationResult.postEmergentTiming?.growthStage,
          post_emergent_status: calculationResult.postEmergentTiming?.status,
        },
        userTier: subscription?.isPremium ? 'premium' : 'free',
        location: calculationResult.zipCode,
        source: 'live_update',
      })
    }
  }

  // Preserve shared/cross-tool URL fidelity: `?type=` (application type) and
  // `?weeds=` (comma-separated names) seed the live form so a shared link
  // reproduces the result it was generated with, not the generic defaults.
  const typeParam = searchParams.get("type")?.toLowerCase()
  const sharedApplicationType =
    typeParam === "pre-emergent" || typeParam === "post-emergent" || typeParam === "both"
      ? typeParam
      : undefined
  const sharedTargetWeeds = (searchParams.get("weeds") ?? "")
    .split(",")
    .map((w) => w.trim())
    .filter((name) => name.length > 0 && Boolean(WEED_CHARACTERISTICS[name]))

  return (
    <div ref={startedRef} className="min-h-screen bg-linear-to-br from-green-50 via-white to-gray-50">
      <div
        className="mx-auto max-w-4xl px-4 pt-4 pb-8 sm:px-6 md:pt-8 md:pb-16 lg:px-8"
      >
        <BreadcrumbComponent
          items={[
            { label: "Home", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: "Herbicide Timing Calculator" },
          ]}
        />

        <div className="mt-6">
          <HeadlineHero
            eyebrow="Personalized for your ZIP"
            title="When should you apply?"
            accent="Down to the day."
            subtitle="Your soil, your weeds, your grass. We tell you exactly when to prevent weeds before they sprout, or treat the ones already up. Works for liquids and granular."
          />
        </div>

        <Card id="herbicide-timing-form" className="mb-10 scroll-mt-24 rounded-2xl border-gray-200 p-4 shadow-md sm:p-6 md:p-8">
          <LawnPrefillBanner lawns={lawns} selectedLawnId={selectedLawnId} onSelect={setSelectedLawnId} />
          <HerbicideTimingForm
            initialValues={{
              zipCode: searchParams.get('zip') ?? lawnPrefill.zipCode,
              grassType: searchParams.get('grass') ?? lawnPrefill.grassType,
              lawnSize: searchParams.get('size') ?? lawnPrefill.lawnSizeSqft,
              sunlight: searchParams.get('sunlight') ?? lawnPrefill.sunlight,
              applicationType: sharedApplicationType,
              targetWeeds: sharedTargetWeeds.length > 0 ? sharedTargetWeeds : undefined,
            }}
            onResultChange={handleResultChange}
          />
        </Card>

        {result && (
          <div id="results" className="space-y-6">
            <HerbicideSuccessCard result={result} />
            {/* Grass-ID handoff, only while the grass is our ZIP-based guess.
                Herbicide safety is grass-specific, so confirming the grass is a
                bigger next step than any sibling tool. Once the user supplies a
                grass this disappears and RelatedTools (page bottom) is the only
                next-step surface again. */}
            {grassTypeSource === 'default' && (
              <ToolNextStepCard
                toolId="herbicide-timing"
                assumedGrassLabel={result.grassType}
                heading={`Is ${result.grassType} actually your grass?`}
                tagline="We guessed that from your ZIP. Snap a photo to confirm it, because the grass you have decides which weed killers are safe to spray on it."
              />
            )}
            <ResultEmailCaptureCard
              source="tool_result_herbicide"
              toolDisplayName="herbicide plan"
              subcopy={`Your 12-month ${result.grassType} lawn calendar, starting with this month's tasks. Arrives right away.`}
              grassType={result.grassType}
              zipCode={result.zipCode}
              accountLead={{
                redirectTo: buildToolUrl("herbicide-timing", {
                  grassType: result.grassType,
                  zip: result.zipCode,
                }),
                headline: `Save my herbicide plan for ${result.grassType}`,
                subcopy: "Keep this herbicide plan in a free account and track your lawn over time.",
                dividerLabel: "or get your 12-month calendar by email",
                onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_herbicide"),
              }}
              onCaptured={() => {
                closeNewsletterModal()
              }}
            />
            <HerbicideTimingResults result={result} weedsWereAutoPicked={weedsWereAutoPicked} />
            <ProductGuideCta source="herbicide_timing_result" variant="result" />
          </div>
        )}

        {!result && (
          <div className="space-y-10">
            <ProductRecommendations
              categories={["Weed Control"]}
              title="Popular Weed Control Products"
              description="Top-rated herbicides for common lawn weeds"
              trackingContext="herbicide-timing-pre-submission"
              compact
            />
          </div>
        )}

        <RelatedTools
          currentToolId="herbicide-timing"
          prefillValues={{
            zip: result?.zipCode ?? lawnPrefill.zipCode,
            grassType: result?.grassType ?? lawnPrefill.grassType,
            lawnSize: lawnPrefill.lawnSizeSqft,
            sunlight: searchParams.get('sunlight') ?? lawnPrefill.sunlight,
            applicationType: result?.applicationType,
            targetWeeds: result?.targetWeeds,
          }}
        />

      </div>

      {/* Newsletter signup modal - shows after 1st calculator use with 3.5s delay */}
      <NewsletterSignupModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
        toolName="Herbicide Timing Calculator"
        usageCount={usageCount}
        grassType={result?.grassType}
        zipCode={result?.zipCode}
      />
    </div>
  )
}

/**
 * Bounded peak-moment frame for the herbicide result. No single headline
 * number — the output is a timing window — so heroValue is intentionally
 * omitted. Interpretation names the grass, the target weeds, and the optimal
 * window for the recommended approach. Next-action: soil-temperature (pre-
 * emergent timing depends on soil temp hitting the threshold).
 */
function HerbicideSuccessCard({ result }: { result: HerbicideTimingResult }) {
  const weedList =
    result.targetWeeds.length === 1
      ? result.targetWeeds[0]
      : result.targetWeeds.length === 2
        ? result.targetWeeds.join(" and ")
        : `${result.targetWeeds.slice(0, -1).join(", ")}, and ${result.targetWeeds.at(-1)}`

  const optimalWindow =
    result.recommendedApproach === "pre-emergent" && result.preEmergentTiming
      ? result.preEmergentTiming.optimalWindow
      : result.recommendedApproach === "post-emergent" && result.postEmergentTiming
        ? result.postEmergentTiming.optimalWindow
        : result.preEmergentTiming?.optimalWindow ?? result.postEmergentTiming?.optimalWindow ?? null

  const soilTempHref = buildRelatedToolUrl("soil-temperature", "herbicide-timing", {
    zip: result.zipCode,
  })

  // Impression denominator for the success-card CTA click below — without it
  // tool_related_link_clicked has no rate.
  const successCardRef = useImpressionEvent<HTMLDivElement>(
    "herbicide-timing",
    undefined,
    {
      emit: () =>
        toolNavigationAnalytics.relatedToolsViewed({
          sourceTool: "herbicide-timing",
          placement: "success_card",
          toolCount: 1,
        }),
    },
  )

  const handleCtaClick = () => {
    toolNavigationAnalytics.relatedToolClicked({
      sourceTool: "herbicide-timing",
      targetTool: "soil-temperature",
      rank: 1,
      placement: "success_card",
      recommendationReason: "Pre-emergent timing depends on soil temperature",
      prefillFields: result.zipCode ? "zip" : "",
    })
  }

  return (
    <div ref={successCardRef}>
    <ResultSuccessCard
      eyebrow="Your application window"
      interpretation={
        <>
          {result.grassType} lawn targeting {weedList}: {result.nextAction}
          {optimalWindow ? ` Optimal window is ${optimalWindow}.` : ""}
        </>
      }
      cta={{
        href: soilTempHref,
        label: "Check soil temperature",
        onClick: handleCtaClick,
      }}
    />
    </div>
  )
}

// Default export with Suspense boundary for useSearchParams
export function HerbicideTimingPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="herbicide-timing" />}>
      <SearchParamsHydrationGate dateSensitive>
        <HerbicideTimingContent />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
